<template>
  <nav class="navbar navbar-expand-lg navbar-light bg-header fixed-top" width="256" style="z-index: 1030;">
    <div class="container-fluid px-3">
      <a class="navbar-brand" href="#">
        <img :src="navbarLogo" alt="Allocore" class="navbar-logo me-2">
      </a>

      <div v-if="isAuthenticated" class="ms-auto">
        <ul class="d-flex align-items-center m-0">
          <li class="d-flex flex-row align-items-center me-3">
            <span class="me-2">Entity:</span>
            <button @click="openSwitchTenantDialog" class="btn btn-light brand-text-primary">
              <span class="fw-semibold">{{ tenantName }}</span>
              <v-icon class="ms-2 cursor-pointer">mdi-arrow-down-drop-circle-outline</v-icon>
            </button>
          </li>
          <li class="nav-item dropdown">
            <button
              class="btn btn-light  d-flex align-items-center brand-text-primary"
              href="#"
              role="button"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              <v-icon class="me-1" color="secondary">mdi-account</v-icon>
              <span class="tw:text-sm tw:font-bold">{{ userName }}</span>
            </button>
            <ul class="dropdown-menu dropdown-menu-end">
              <li>
                <button class="dropdown-item disabled" href="#" tabindex="-1" aria-disabled="true">
                  <v-icon size="small" class="me-2">mdi-cog</v-icon>
                  Settings
                </button>
              </li>
              <li>
                <hr class="dropdown-divider">
              </li>
              <li>
                <button class="dropdown-item" href="#" @click.prevent="handleLogout">
                  <v-icon size="small" class="me-2">mdi-logout</v-icon>
                  Logout
                </button>
              </li>
            </ul>
          </li>
        </ul>
      </div>
    </div>

    <!-- Switch Tenant Dialog -->
    <v-dialog v-model="switchTenantDialog" persistent max-width="400">
      <v-card>
        <v-card-title class="d-flex justify-center pt-4"><span class="fw-bold">Switch Tenant</span></v-card-title>
        <v-card-text>
          <div class="mb-3">
            <label class="form-label mandatory">Tenant Name</label>
            <v-select
              v-model="newTenant"
              :items="tenants"
              item-title="tenantName"
              item-value="tenantId"
              variant="outlined"
              density="comfortable"
              clearable
            />
          </div>
        </v-card-text>
        <v-card-actions class="d-flex justify-end gap-2 pb-4">
          <button class="btn btn-outline-secondary" @click="closeSwitchTenantDialog">Cancel</button>
          <button class="btn btn-primary" :disabled="!newTenant || asyncDisable" @click="handleSwitchTenant">
            Switch Tenant
          </button>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </nav>
</template>

<script setup lang="ts">
  import { useAuth } from '@composables/useAuth';
  import { useSidebarState } from '@composables/useSidebarState';
  import { useTheme } from '@composables/useTheme';
  import { TenantService } from '@services/TenantService';
  import { getErrorMessage } from '@utils/error-utils';
  import { computed, onUnmounted, ref } from 'vue';

  const { username, tenantName, isAuthenticated, logout, switchTenant } = useAuth();
  const { navbarLogo } = useTheme();
  const tenantService = new TenantService();

  const switchTenantDialog = ref(false);
  const asyncDisable = ref(false);
  const newTenant = ref<string | null>(null);
  const tenants = ref<{ tenantId: string; tenantName: string }[]>([]);

  const userName = computed(() => username.value);

  const openSwitchTenantDialog = async (): Promise<void> => {
    await populateTenants();
    switchTenantDialog.value = true;
  };

  const closeSwitchTenantDialog = (): void => {
    switchTenantDialog.value = false;
    newTenant.value = null;
  };

  const handleSwitchTenant = async (): Promise<void> => {
    if (!newTenant.value) return;

    asyncDisable.value = true;
    try {
      const selectedTenant = tenants.value.find((t) => t.tenantId === newTenant.value);
      if (selectedTenant) {
        await switchTenant({
          tenantId: selectedTenant.tenantId,
          tenantName: selectedTenant.tenantName,
        });
        closeSwitchTenantDialog();
      }
    } catch (error) {
      console.error('Error switching tenant:', getErrorMessage(error as Error));
    } finally {
      asyncDisable.value = false;
    }
  };

  const populateTenants = async (): Promise<void> => {
    if (!username.value) return;

    try {
      const res = await tenantService.getUserTenantsPublic(username.value);
      if (!res.success) throw res.err;
      tenants.value = (res.response ?? [])
        .map((tenant) => ({
          tenantName: (tenant?.tenantName || tenant?.tenantId) as string,
          tenantId: tenant?.tenantId as string,
        }))
        .filter((t) => t.tenantId);

      if (tenants.value.length > 0) {
        newTenant.value = tenantName.value;
        tenants.value.sort((a, b) => (a.tenantName > b.tenantName ? 1 : -1));
      }
    } catch (error) {
      console.error('Error populating tenants:', getErrorMessage(error as Error));
    }
  };

  const handleLogout = async (): Promise<void> => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', getErrorMessage(error as Error));
    }
  };

  onUnmounted(() => {
    tenantService.cancelRequest();
  });
</script>

<style scoped lang="scss">
$navbar-height: 70px;

.navbar {
  z-index: 1030;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  min-height: $navbar-height;
  height: $navbar-height;
}

// .navbar-brand {
//   font-size: 1.1rem;
//   height: 100%;
//   display: flex;
//   align-items: center;
// }

.navbar-logo {
  height: 2.5rem;
  width: auto;
  display: block;
  object-fit: contain;
}

.sidebar-toggle {
  width: 32px;
  height: 32px;
  padding: 0;
}

.mandatory::after {
  content: '*';
  color: red;
  margin-left: 0.25rem;
}
</style>
